import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BsArrowUpRightSquare, BsCheckCircleFill } from "react-icons/bs";
import { FaRegMap } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import { useAppNavigate, useAppSelector } from "@app/hooks";
import { selectProjectAreaDefined } from "@features/project/projectSlice";
import TranslatableText from "@components/translations/TranslatableText";
import Button from "@components/button/Button";
import type { Project } from "@typedefs/interfaces";

interface ProjectAreaBannerProps {
  project: Project | null;
}

const ProjectAreaBanner = ({ project }: ProjectAreaBannerProps) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { pathname } = useLocation();
  const projectAreaDefined = useAppSelector(selectProjectAreaDefined);

  if (projectAreaDefined === null) {
    return <Skeleton height={56} borderRadius={6} className="mt-2" />;
  }

  const handleOpenMap = () => {
    if (project?.id) {
      navigate({ path: `/bintact-builder/${project.id}`, options: { state: { from: pathname } } });
    }
  };

  if (!projectAreaDefined) {
    return (
      <div className="project-area-banner project-area-banner--undefined mt-2">
        <div className="project-area-banner__icon-wrap">
          <FaRegMap size={22} color="#D97706" />
        </div>
        <div className="project-area-banner__body">
          <div className="project-area-banner__title-row">
            <span className="project-area-banner__title ff-bold fs-12">
              <TranslatableText translationKey="map.define_project_area" />
            </span>
            <span className="project-area-banner__badge">
              <TranslatableText translationKey="map.define_project_area_badge" />
            </span>
          </div>
          <p className="project-area-banner__desc fs-10">
            <TranslatableText translationKey="map.define_project_area_description" />
          </p>
        </div>
        <Button
          type="button"
          className="project-area-banner__action project-area-banner__action--link"
          onClick={handleOpenMap}
        >
          <TranslatableText translationKey="map.open_map" />
          <BsArrowUpRightSquare size={13} className="ml-1" />
        </Button>
      </div>
    );
  }

  const statsparts: string[] = [];
  statsparts.push(t("map.project_area_baseline_ready"));

  return (
    <div className="project-area-banner project-area-banner--defined mt-2">
      <BsCheckCircleFill size={20} color="var(--bintact-bright)" className="project-area-banner__check" />
      <div className="project-area-banner__body">
        <span className="project-area-banner__title ff-bold fs-13">
          <TranslatableText translationKey="map.project_area_defined" />
        </span>
        <p className="project-area-banner__desc fs-12">
          {statsparts.join(" · ")}
        </p>
      </div>
      <div className="d-flex h-100 align-items-center">
        <Button
          type="button"
          className="project-area-banner__action project-area-banner__action--btn"
          onClick={handleOpenMap}
        >
          <TranslatableText translationKey="map.open_map" className="fs-11" />
          <BsArrowUpRightSquare size={16}  style={{ marginLeft: '4px' }}/>
        </Button>
      </div>
    </div>
  );
};

export default ProjectAreaBanner;
